import {
    DateTimeUtils,
    EntityFactory,
    EntityType,
    InstanceFactory,
    IPage,
    Label,
    Page,
    PageFactory,
    PageLibraryFactory,
    ServerSidePaginatedLoadingInput
} from "@amzn/ask-legal-domain";
import {
    Badge,
    Box,
    Button,
    ButtonDropdown,
    ButtonDropdownProps,
    ColumnLayout,
    Container,
    Flashbar,
    FlashbarProps,
    Form,
    FormField,
    Grid,
    Header,
    SpaceBetween,
    Spinner,
    StatusIndicator,
    TextContent,
    TokenGroup,
    Link
} from "@amzn/awsui-components-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { EntityPermissionPolarisFactory } from "../../factory/polaris/entity-permission-polaris-factory";
import { useAPI, useAPI2 } from "../../hooks/api-hook";
import { PageModel } from "../../model/page-model";
import { UIModel } from "../../model/ui-model";
import { AppContext } from "../../setup/context";
import { DeprecationModal } from "../common/DeprecationModal";
import { DeleteModal } from "../common/DeleteModal";
import { UIField } from "../common/UIField";
import { UserSearch } from "../common/UserSearch";
import { TeamSearch } from "../common/TeamSearch";
import { LabelAssignModal } from "../label/LabelAssignModal";
import { DeepCopyPageModal } from "./modal-content/DeepCopyPageModal";
import { APIOutput, Instance } from "@amzn/ask-legal-domain";

const SECONDARY_OWNER_LIMIT = 5;

export const PageOverviewTab = (
    props: {
        state: UIModel.State<Page.Data>;
        reload: () => void;
        setActiveTab: (tab: string) => void;
    }
) => {
    const navigate = useNavigate();
    const context = React.useContext(AppContext);
    const checkFreshnessRunner = useAPI2(
        context.getPageAPI().checkFreshness
    );
    const updatePageMetadataRunner = useAPI(
        context.getPageAPI().updateMetadata
    );
    const baselinePagePermissionsRunner = useAPI(
        context.getPageAPI().updateMetadata
    );
    const updatePageMetadataState = PageModel.UpdateMetadataState.use({
        template: props.state.value
    });
    const [editing, setEditing] = React.useState<boolean>(false);
    const [modalType, setModalType] = React.useState<string>();
    const [pageFreshnessStatus, setPageFreshnessStatus] = React.useState<Page.Freshness>();
    const [freshnessUnavailable, setFreshnessUnavailable] = React.useState(false);
    const [timeToExpire, setTimeToExpire] = React.useState<number>();
    const [displayUpdates, _setDisplayUpdates] = React.useState<Array<FlashbarProps.MessageDefinition>>([]);

    const freshPageStatus = <b><span style={{color: "green"}}>Fresh</span></b>;
    const expiredPageStatus = <b><span style={{color: "red"}}>Expired</span></b>;

    const loadLabelsRunner = useAPI2(context.getLabelAPI().getLabelsByResource);
    const loadPageLibraryRunner = useAPI(context.getPageLibraryAPI().load);
    const loadInstanceRunner = useAPI2(context.getInstanceAPI().load);
    const pageRemindersEnabled = loadInstanceRunner.output?.preferences?.notifications?.sendPageFreshnessReminders;

    // Prefetch instance list for cross-instance copy modal
    const [prefetchedInstances, setPrefetchedInstances] = React.useState<{ label: string; value: string }[]>(null);
    React.useEffect(() => {
        context.getInstanceAPI().loadAll().then(res => {
            const output = APIOutput.fromRaw<Instance[]>(res.data);
            if (output.isOk()) {
                setPrefetchedInstances(output.data.map(inst => ({
                    label: inst.name || inst.id,
                    value: inst.id
                })).sort((a, b) => a.label.localeCompare(b.label)));
            }
        }).catch(() => { /* prefetch failure is non-critical */ });
    }, []);

    const addDisplayUpdate = (message: FlashbarProps.MessageDefinition) => {
        _setDisplayUpdates((prev) => [
            message,
            ...prev.filter((msg) => msg.id !== message.id)
        ]);
    };

    const removeDisplayUpdateById = (messageId: string) => {
        _setDisplayUpdates((prev) => prev.filter((message) => message.id !== messageId));
    };

    const updatePage = () => {
        updatePageMetadataRunner.submitRun(
            PageModel.UpdateMetadataState.toInput(updatePageMetadataState)
        );
    };

    const getPageExpiryTimeinDays = (pageLibraryFreshness: number, pageFreshnessDate: Date) => {
        if (!pageLibraryFreshness || pageLibraryFreshness <= 0 || !pageFreshnessDate) {
            return -1;
        }
        const now = new Date();
        return pageLibraryFreshness - DateTimeUtils.getDatesDiffinDays(now, new Date(pageFreshnessDate));
    };

    const baselineFreshness = () => {
        updatePageMetadataRunner.submitRun(
            IPage.UpdatePageMetadataInput.create({
                baselineFreshness: true,
                targetRef: EntityFactory.toEntityRef(props.state.value)
            })
        );
    };

    const baselinePagePermissions = () => {
        baselinePagePermissionsRunner.submitRun(
            IPage.UpdatePageMetadataInput.create({
                baselinePagePermissions: true,
                targetRef: EntityFactory.toEntityRef(props.state.value)
            })
        );
    };

    const baselineFreshnessActionButton = () => {
        return (
            <Button
                onClick={baselineFreshness}
                loading={updatePageMetadataRunner.status === "Running" || loadPageLibraryRunner.status === "Running"}
                disabled={props.state.value.deprecated || loadPageLibraryRunner.status === "Error" || baselinePagePermissionsRunner.status === "Running"}
                >
                Baseline page
            </Button>
        );
    };

    const checkFreshness = () => {
        checkFreshnessRunner.invoke(props.state.value.id);
    };

    const labelState = UIModel.State.useArray<Label.Data>({
        initialValue: []
    });

    const getButtonDropdownItemGroups = (): ButtonDropdownProps.Items => {
        const maintenanceItems: ButtonDropdownProps.Items = [
            { id: "activate", text: "Activate page", disabled: !props.state.value.deprecated },
            { id: "baselineFreshness", text: "Baseline page", disabled: props.state.value.deprecated || baselinePagePermissionsRunner.status === "Running" || updatePageMetadataRunner.status === "Running" },
            { id: "baselinePagePermissions", text: "Baseline permissions", disabled: props.state.value.deprecated || updatePageMetadataRunner.status === "Running" || baselinePagePermissionsRunner.status === "Running" },
            { id: "deprecate", text: "Deprecate page", disabled: props.state.value.deprecated },
            { id: "editPageOwner", text: "Edit page owner", disabled: false },
            { id: "editPageInfo", text: "Edit page title & description", disabled: false },
            { id: "manageLabels", text: "Manage labels", disabled: false }
        ];
        let administrationItems: ButtonDropdownProps.Items = [
            { id: "deepCopy", text: "Copy page", disabled: props.state.value.deprecated, iconName: "copy" },
            { id: "delete", text: "Delete page", disabled: false, iconName: "remove" },
            { id: "checkFreshness", text: "Send freshness reminder email", disabled: props.state.value.deprecated, iconName: "envelope" }
        ];
        return [
            { text: "Maintenance", items: maintenanceItems },
            { text: "Administration", items: administrationItems }
        ];
    };

    const pageActionsClickHandler = (event: { detail: { id: string } }) => {
        switch (event.detail.id) {
            case "editPageOwner":
                updatePageMetadataState.reset();
                setEditing(true);
                break;
            case "editPageInfo":
                props.setActiveTab("draftPage");
                navigate(`/page/${props.state.value.id}/admin?tab=draftPage&mode=editPageInfo`);
                break;
            case "checkFreshness":
                checkFreshness();
                break;
            case "baselineFreshness":
                baselineFreshness();
                break;
            case "baselinePagePermissions":
                baselinePagePermissions();
                break;
            case "deprecate":
            case "activate":
            case "delete":
            case "manageLabels":
            case "deepCopy":
                setModalType(event.detail.id);
                break;
            default: break;
        }
    };

    const BaseLineReminderUI = (
        linkHref: string,
        remindersEnabled: boolean
    ): React.ReactNode => {
        return (
            <Box fontSize="body-m">
                Baseline notifications are <b>{remindersEnabled ? "enabled" : "disabled"}</b>.&nbsp;
                <Link href={linkHref} external>
                    Click here
                </Link>
                to manage instance notification preferences.
            </Box>
        );
    };

    React.useEffect(() => {
        loadPageLibraryRunner.submitRun(props.state.value.pageLibraryId);
        loadLabelsRunner.invoke(ServerSidePaginatedLoadingInput.create({
            partitionKey: props.state.value.id,
            pageSize: 100,
            currentPageIndex: 1,
            filters: []
        }));
    }, []);

    React.useEffect(() => {
        // Update freshness whenever any page update occur
        loadPageLibraryRunner.reload();
        setPageFreshnessStatus(undefined);
        setTimeToExpire(undefined);
    }, [props.state.value]);

    React.useEffect(() => {
        if (loadLabelsRunner.status === "Succeeded") {
            labelState.setValue(loadLabelsRunner.output.result);
        }
    }, [loadLabelsRunner.status]);

    React.useEffect(() => {
        loadInstanceRunner.invoke(InstanceFactory.fromEntityId(props.state.value.id));
    }, [props.state.value.id]);

    React.useEffect(() => {
        if (checkFreshnessRunner.status === "Succeeded") {
            setEditing(false);
            addDisplayUpdate({
                id: "checkFreshnessRunner",
                type: "success",
                content: checkFreshnessRunner.output ?
                    `Page is in expired status, and email notification sent to Page owner ${props.state.value.pageOwner.id}` :
                    `Page is up-to-date, no email has been sent`,
                dismissible: true,
                onDismiss: () => removeDisplayUpdateById("checkFreshnessRunner")
            });
        } else if (checkFreshnessRunner.status === "Error") {
            addDisplayUpdate({
                id: "checkFreshnessRunner",
                type: "error",
                content: `Failed checking page freshness or sending email. Reason: ${checkFreshnessRunner.err.message}`,
                dismissible: true,
                onDismiss: () => removeDisplayUpdateById("checkFreshnessRunner")
            });
        } else if (checkFreshnessRunner.status === "Running") {
            addDisplayUpdate({
                id: "checkFreshnessRunner",
                type: "in-progress",
                content: "Checking and sending email....",
                loading: true
            });
        }
    }, [checkFreshnessRunner.status]);

    React.useEffect(() => {
        if (updatePageMetadataRunner.status === "Succeeded") {
            setEditing(false);
            props.state.setValue(updatePageMetadataRunner.data.output);
            addDisplayUpdate({
                id: "updatePage",
                type: "success",
                content: updatePageMetadataRunner.data.input.baselineFreshness ?
                    `Page freshness successfully baselined` : `Page details updated successfully`,
                dismissible: true,
                onDismiss: () => removeDisplayUpdateById("updatePage")
            });
        } else if (updatePageMetadataRunner.status === "Error") {
            updatePageMetadataState.reset();
            addDisplayUpdate({
                id: "updatePage",
                type: "error",
                content: `${updatePageMetadataRunner.data.input.baselineFreshness ?
                    `Failed to baseline freshness.` :
                    `Failed to update page details.`} Reason: ${updatePageMetadataRunner.data.err.message}`,
                dismissible: true,
                onDismiss: () => removeDisplayUpdateById("updatePage")
            });
        } else if (updatePageMetadataRunner.status === "Running") {
            addDisplayUpdate({
                id: "updatePage",
                type: "in-progress",
                content: updatePageMetadataRunner.data.input.baselineFreshness ?
                    `Baselining page freshness...` : `Updating page details...`,
                loading: true
            });
        }
    }, [updatePageMetadataRunner.status]);

    React.useEffect(() => {
        if (baselinePagePermissionsRunner.status === "Succeeded") {
            props.state.setValue(baselinePagePermissionsRunner.data.output);
            addDisplayUpdate({
                id: "baselinePagePermissions",
                type: "success",
                content: "Page permissions successfully baselined",
                dismissible: true,
                onDismiss: () => removeDisplayUpdateById("baselinePagePermissions")
            });
        } else if (baselinePagePermissionsRunner.status === "Error") {
            addDisplayUpdate({
                id: "baselinePagePermissions",
                type: "error",
                content: `Failed to baseline permissions. Reason: ${baselinePagePermissionsRunner.data.err.message}`,
                dismissible: true,
                onDismiss: () => removeDisplayUpdateById("baselinePagePermissions")
            });
        } else if (baselinePagePermissionsRunner.status === "Running") {
            addDisplayUpdate({
                id: "baselinePagePermissions",
                type: "in-progress",
                content: "Baselining page permissions...",
                loading: true
            });
        }
    }, [baselinePagePermissionsRunner.status]);

    React.useEffect(() => {
        if (loadPageLibraryRunner.status === "Succeeded") {
            const status = PageFactory.getPageFreshnessStatus(props.state.value, loadPageLibraryRunner.data.output.freshness);
            if (status === Page.Freshness.EXPIRED && !props.state.value.deprecated) {
                addDisplayUpdate({
                    id: "loadPageLibraryRunner",
                    type: "warning",
                    dismissible: false,
                    header: "This page's freshness status is expired!",
                    content: "Please baseline or publish a new version to set the page to fresh.",
                    action: baselineFreshnessActionButton()
                });
            } else {
                removeDisplayUpdateById("loadPageLibraryRunner");
            }
            setPageFreshnessStatus(status);
            setTimeToExpire(getPageExpiryTimeinDays(loadPageLibraryRunner.data.output.freshness, props.state.value.freshnessDate));
        } else if (loadPageLibraryRunner.status === "Error") {
            const err = loadPageLibraryRunner.data.err;
            if (err?.code === 403) {
                // Page-level editors don't have PL access — freshness is a PL-level
                // concern, so silently suppress. No action needed from the user.
                removeDisplayUpdateById("loadPageLibraryRunner");
                setFreshnessUnavailable(true);
            } else {
                addDisplayUpdate({
                    id: "loadPageLibraryRunner",
                    type: "error",
                    content: `Failed to fetch freshness status. Reason: ${err?.message}`,
                    dismissible: true,
                    onDismiss: () => removeDisplayUpdateById("loadPageLibraryRunner")
                });
            }
        } else if (loadLabelsRunner.status === "Running") {
            addDisplayUpdate({
                id: "loadPageLibraryRunner",
                type: "in-progress",
                content: "Fetching page freshness status",
                loading: true
            });
        }
    }, [loadPageLibraryRunner.status]);

    const renderModalContent = () => {
        switch (modalType) {
            case "activate":
            case "deprecate":
                return <DeprecationModal
                    entityRef={props.state.value}
                    deprecated={props.state.value.deprecated}
                    forwardingUrl={props.state.value.forwardingUrl}
                    onCancel={() => setModalType(null)}
                    onUpdated={(entity: Page.Data) => {
                        setModalType(null);
                        props.state.setValue(entity);
                    }}
                />;
            case "delete":
                return <DeleteModal
                    entityRef={props.state.value}
                    onCancel={() => setModalType(null)}
                    onDeleted={() =>
                        navigate(`/page-library/${PageLibraryFactory.fromEntityId(props.state.value.id)}/admin`)
                    }
                    customHeader="Delete Page"
                    customMessage={<strong><em>
                        This operation will permanently delete all resources on this page including permissions and page drafts.
                    </em></strong>}
                />;
            case "deepCopy":
                return <DeepCopyPageModal
                    onDismiss={() => {
                        setModalType(null);
                        props.reload();
                    }}
                    pageId={props.state.value.id}
                    prefetchedInstances={prefetchedInstances}
                />;
            case "manageLabels":
                return <LabelAssignModal
                    resourceId={props.state.value.id}
                    resourceType={EntityType.Page}
                    instanceId={InstanceFactory.fromEntityId(props.state.value.id)}
                    onCloseModal={() => setModalType(null)}
                    onLinkSucceeded={newLabel => labelState.setValue([...labelState.value, newLabel])}
                    onUnlinkSucceeded={deletedId => {
                        const toDeleteIndex = labelState.value.findIndex(l => l.id === deletedId);
                        if (toDeleteIndex >= 0) {
                            const temp = labelState.value.slice();
                            temp.splice(toDeleteIndex, 1);
                            labelState.setValue(temp);
                        }
                    }}
                />;
            default:
                return <StatusIndicator type="error">Not yet implemented</StatusIndicator>;
        }
    };

    return (
        <React.Fragment>
            <SpaceBetween size="m" direction="vertical">
                <Flashbar
                    items={displayUpdates}
                />
                {modalType && renderModalContent()}
                <Container
                    header={
                        <Header
                            variant="h3"
                            actions={!editing &&
                                <SpaceBetween direction="horizontal" size="m">
                                    <ButtonDropdown
                                        items={getButtonDropdownItemGroups()}
                                        onItemClick={pageActionsClickHandler}
                                    >
                                        Page Actions
                                    </ButtonDropdown>
                                </SpaceBetween>
                            }>
                                <SpaceBetween size="s" direction="horizontal">
                                    Page Details
                                    {!props.state.value.deprecated && <StatusIndicator type="success">Active</StatusIndicator>}
                                    {props.state.value.deprecated && <StatusIndicator type="stopped">Deprecated</StatusIndicator>}
                                </SpaceBetween>
                        </Header>}>
                    <Form
                        actions={editing && (
                            <div>
                                <Button onClick={() => {
                                    updatePageMetadataState.reset();
                                    setEditing(false);
                                }} variant="link">Cancel</Button>
                                <Button
                                    variant="primary"
                                    onClick={updatePage}
                                    disabled={
                                        !updatePageMetadataState.isDirty() ||
                                        updatePageMetadataState.pageOwner.errorText ||
                                        updatePageMetadataState.forwardingUrl.errorText ||
                                        updatePageMetadataState.secondaryPageOwners.value?.length > SECONDARY_OWNER_LIMIT ||
                                        updatePageMetadataState.secondaryPageOwnerTeams.value?.length > SECONDARY_OWNER_LIMIT
                                    }
                                    loading={updatePageMetadataRunner.status === "Running"}
                                >Submit</Button>
                            </div>
                        )}>
                        <ColumnLayout columns={2}>
                            <SpaceBetween direction="vertical" size="xl">
                                <UIField.ValueField
                                    name="Name (part of the URL)"
                                    value={props.state.value.name}
                                />
                                <UIField.LabelField
                                    label="Page Owner"
                                    children={
                                        !editing || updatePageMetadataRunner.status === "Running" ?
                                            <TokenGroup
                                                items={!!updatePageMetadataState.pageOwner.value &&
                                                    EntityPermissionPolarisFactory.TokenGroup.toUserTokens(
                                                        [updatePageMetadataState.pageOwner.value])}
                                                onDismiss={({ detail: { itemIndex }}) => {
                                                    setEditing(true);
                                                }}
                                            /> :
                                            <Box padding={{ top: "xxxs" }}>
                                                <Grid gridDefinition={[{
                                                    colspan: 10
                                                }]}>
                                                    <UserSearch.Single
                                                        selected={updatePageMetadataState.pageOwner.value}
                                                        onUserSelectChange={(selected) => updatePageMetadataState.pageOwner.setValue(selected)}
                                                    />
                                                </Grid>
                                            </Box>}
                                />
                                {props.state.value.deprecated && !!props.state.value.forwardingUrl &&
                                    <UIField.StateValueField
                                        name="Deprecated forwarding URL"
                                        placeholder="Deprecated forwarding URL (optional)"
                                        state={updatePageMetadataState.forwardingUrl}
                                        editing={editing && updatePageMetadataRunner.status !== "Running"}
                                    />
                                }
                                <UIField.LabelField
                                    label="Labels"
                                    children={labelState.value.map(label => <Badge>{label.name}</Badge>)}
                                />
                                <FormField
                                    label="Secondary Page Owners (Users)"
                                    constraintText={editing ? `Maximum ${SECONDARY_OWNER_LIMIT} users` : undefined}
                                    errorText={updatePageMetadataState.secondaryPageOwners.value?.length > SECONDARY_OWNER_LIMIT
                                        ? `Maximum ${SECONDARY_OWNER_LIMIT} secondary page owners allowed` : undefined}
                                >
                                    {!editing || updatePageMetadataRunner.status === "Running" ?
                                        <TokenGroup
                                            items={updatePageMetadataState.secondaryPageOwners.value?.length > 0
                                                ? EntityPermissionPolarisFactory.TokenGroup.toUserTokens(
                                                    updatePageMetadataState.secondaryPageOwners.value)
                                                : []}
                                        /> :
                                        <UserSearch.Multiple
                                            initialSelected={updatePageMetadataState.secondaryPageOwners.value}
                                            onUserSelectChange={(selected) => {
                                                updatePageMetadataState.secondaryPageOwners.setValue(selected);
                                            }}
                                        />
                                    }
                                </FormField>
                                <FormField
                                    label="Secondary Page Owner Teams"
                                    constraintText={editing ? `Maximum ${SECONDARY_OWNER_LIMIT} teams` : undefined}
                                    errorText={updatePageMetadataState.secondaryPageOwnerTeams.value?.length > SECONDARY_OWNER_LIMIT
                                        ? `Maximum ${SECONDARY_OWNER_LIMIT} secondary page owner teams allowed` : undefined}
                                >
                                    {!editing || updatePageMetadataRunner.status === "Running" ?
                                        <TokenGroup
                                            items={updatePageMetadataState.secondaryPageOwnerTeams.value?.length > 0
                                                ? EntityPermissionPolarisFactory.TokenGroup.toTeamTokens(
                                                    updatePageMetadataState.secondaryPageOwnerTeams.value)
                                                : []}
                                        /> :
                                        <TeamSearch.Multiple
                                            initialSelected={updatePageMetadataState.secondaryPageOwnerTeams.value}
                                            onTeamSelectChange={(selected) => {
                                                updatePageMetadataState.secondaryPageOwnerTeams.setValue(selected);
                                            }}
                                        />
                                    }
                                </FormField>
                            </SpaceBetween>
                            <SpaceBetween direction="vertical" size="l">
                                <UIField.ValueField
                                    name="Title"
                                    value={props.state.value.title}
                                />
                                <UIField.ValueField
                                    name="Description"
                                    value={props.state.value.description}
                                />
                                {!pageFreshnessStatus && !freshnessUnavailable && loadPageLibraryRunner.status !== "Error" && <Spinner variant="normal"/>}
                                {!pageFreshnessStatus && !freshnessUnavailable && loadPageLibraryRunner.status === "Error" && <StatusIndicator type="error">Failed</StatusIndicator>}
                                {!!pageFreshnessStatus && pageFreshnessStatus === "Expired" &&
                                    <UIField.LabelField label="Freshness Status">
                                        <TextContent>
                                            {expiredPageStatus}
                                        </TextContent>
                                    </UIField.LabelField>
                                }
                                {!!pageFreshnessStatus && pageFreshnessStatus === "Fresh" &&
                                    <UIField.LabelField label="Freshness Status (Expires in)">
                                        <TextContent>
                                            {freshPageStatus} {!timeToExpire && timeToExpire !== 0 && <Spinner variant="disabled" />}{
                                            !!timeToExpire && timeToExpire < 0 ? <small> N/A</small> : <small> {timeToExpire} days</small>}
                                        </TextContent>
                                    </UIField.LabelField>
                                }
                                {loadInstanceRunner.status === "Succeeded" && (
                                    <FormField
                                        description={BaseLineReminderUI(
                                            `#/instance/${InstanceFactory.fromEntityId(props.state.value.id)}/admin?tab=preferences`,
                                            pageRemindersEnabled
                                        )}
                                    />
                                )}
                                {/* Hidden when unset: baselines before freshnessBaselinedDate existed were not recorded. */}
                                {props.state.value.freshnessBaselinedDate && (
                                    <UIField.ValueField
                                        name="Last Baselined"
                                        value={`${new Date(props.state.value.freshnessBaselinedDate).toLocaleDateString()}${
                                            props.state.value.freshnessBaselinedBy?.name ? ` by ${props.state.value.freshnessBaselinedBy.name}` : ""}`}
                                    />
                                )}
                                <UIField.ValueField
                                    name="Last Permissions Baselined"
                                    value={props.state.value.permissionsBaselinedDate
                                        ? new Date(props.state.value.permissionsBaselinedDate).toLocaleDateString()
                                        : "Never"}
                                />
                            </SpaceBetween>
                        </ColumnLayout>
                    </Form>
                </Container>
            </SpaceBetween>
        </React.Fragment>
    );
};